import { NgClass, NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common'
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { OptionsSettingsGroup } from '../options-settings.groups'

export type OptionsLeafKind = 'boolean' | 'color' | 'number' | 'string' | 'url' | 'html' | 'array' | 'null'

interface RenderSection {
  id: string
  title: string | null
  keys: string[]
  note?: string
  /** Excluded keys that belong to this section — show Add chips inside the card. */
  missingKeys: string[]
}

const FRIENDLY_LABELS: Record<string, string> = {
  theme: 'Theme',
  translate: 'Translations',
  logo: 'Logo URL',
  enable: 'Enabled',
  enabled: 'Enabled',
  status: 'Enabled',
  palette: 'Colors',
  background: 'Background color',
  button: 'Button color',
  cardBackground: 'Card background',
  ara: 'Arabic',
  name: 'Name',
  isWhiteLabeled: 'White labeled',
  invitationSignoff: 'Invitation sign-off',
  front_fixed_element: 'Badge image URL',
  seatsIO: 'Seats.io',
  chart: 'Chart',
  season: 'Season',
  key: 'Key',
  dct: 'DCT',
  dtcm: 'DTCM',
  eventCode: 'Event code',
  sellerCode: 'Seller code',
  triggerOn: 'Trigger on',
  section: 'Section',
  sectionId: 'Section ID',
  priceTypeCode: 'Price type code',
  priceTypePrice: 'Price type price',
  eventId: 'Event ID',
  session: 'Session',
  sessionSlot: 'Session slot',
  product: 'Product',
  productVariant: 'Product variant',
  price: 'Price',
  brella: 'Brella',
  reminders: 'Reminders',
  schedules: 'Schedules',
  api_key: 'API key',
  organization: 'Organization',
  event: 'Event',
  attendee_group: 'Attendee group',
  enable_element: 'Enable element',
  enable_element_value: 'Enable element value',
  locked: 'Lock registration',
  snoball: 'Snoball',
  integrations: 'Integrations',
  vFairs: 'vFairs',
  handshakes: 'Handshakes',
  cardText: 'Card text',
  appStoreLink: 'App Store link',
  playStoreLink: 'Play Store link',
  payment: 'Payment',
  package: 'Package',
  currency: 'Currency',
  quantity: 'Quantity',
  ai_approve: 'AI approval',
  hideQrCode: 'Hide QR code',
  hideDateTime: 'Hide date & time',
  hideLocation: 'Hide location',
  hideEditButton: 'Hide edit button',
  editLabel: 'Edit info button text',
  hideWalletButton: 'Hide wallet button',
  background_preprinted: 'Background preprinted',
  font_family_name: 'Font family',
  barcodeColor: 'Barcode color',
  minimumEntries: 'Minimum entries',
  maximumEntries: 'Maximum entries',
  titles: 'Titles',
  main: 'Main title',
  additional: 'Additional title',
  addLabel: 'Add button label',
  translations: 'Translations',
  allowInvitation: 'Allow invitation',
  allowImport: 'Allow import',
  autoApproveInvitation: 'Auto-approve invitation',
  b2b2go: 'B2B2GO',
  userName: 'Username',
  password: 'Password',
  facialRecognition: 'Facial recognition',
  pendingApprovalMessage: 'Pending approval message',
  pendingPaymentMessage: 'Pending payment message',
  notifyParent: 'Notify parent',
  emailTemplate: 'Email template'
}

const GATE_KEYS = ['enable', 'enabled', 'status'] as const

@Component({
  selector: 'ngx-options-tree-editor',
  standalone: true,
  templateUrl: './options-tree-editor.component.html',
  host: { class: 'tw-block' },
  imports: [NgClass, NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault, FormsModule, OptionsTreeEditorComponent]
})
export class OptionsTreeEditorComponent implements OnChanges, OnInit {
  @Input({ required: true }) node!: Record<string, unknown>
  @Input() pathPrefix = ''
  /** Root-level catalog sections (persona/event/field). Nested editors ignore this. */
  @Input() groups: OptionsSettingsGroup[] = []
  /** Catalog defaults for this node — used to restore excluded keys. */
  @Input() defaults: Record<string, unknown> | null = null
  /** Keys already shown in a parent legend (e.g. enable toggle). */
  @Input() hideKeys: string[] = []
  /** When true, keys outside the catalog groups stay hidden (no Other section). */
  @Input() hideOther = false
  /** Event vs persona — used for translation name labels. Nested editors keep the same scope. */
  @Input() scope: 'event' | 'form' | '' = ''
  @Input() depth = 0
  @Output() unsetPath = new EventEmitter<string>()
  @Output() restorePath = new EventEmitter<string>()

  public sections: RenderSection[] = []
  /** path → error message for invalid URL fields */
  public urlErrors: Record<string, string> = {}
  /** Root accordion: which catalog section is open (one at a time). */
  public openSectionId: string | null = null
  public readonly maxDepth = 8

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['node'] || changes['groups'] || changes['pathPrefix'] || changes['defaults'] || changes['hideKeys'] || changes['hideOther']) {
      this._rebuild()
    }
  }

  ngOnInit(): void {
    if (!this.sections.length) {
      this._rebuild()
    }
  }

  public get isRootLayout(): boolean {
    return !this.pathPrefix && this.depth === 0 && !!this.groups?.length
  }

  public trackSection = (_: number, section: RenderSection): string => section.id

  public trackKey = (_: number, key: string): string => key

  public pathFor(key: string): string {
    return this.pathPrefix ? `${this.pathPrefix}.${key}` : key
  }

  public labelFor(key: string): string {
    if (key === 'name' && /(^|\.)translate\.ara$/.test(this.pathPrefix)) {
      return this.scope === 'event' ? 'Event name' : 'Persona name'
    }
    if (FRIENDLY_LABELS[key]) {
      return FRIENDLY_LABELS[key]
    }
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\./g, ' · ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
  }

  public isPlainObject(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
  }

  /**
   * Skip redundant wrapper fieldset when object key matches the catalog section
   * (e.g. section "Integrations" + key `integrations` → show vFairs directly).
   */
  public shouldHoistObject(key: string, section: RenderSection): boolean {
    if (!section?.title || !this.isPlainObject(this.node[key])) {
      return false
    }
    if (key === section.id) {
      return true
    }
    return this.labelFor(key).toLowerCase() === section.title.toLowerCase()
  }

  public isAccordionOpen(sectionId: string): boolean {
    if (!this.isRootLayout) {
      return true
    }
    return this.openSectionId === sectionId
  }

  public jumpToSection(sectionId: string): void {
    this.openSectionId = sectionId
    // Single-panel root: no scrollIntoView — avoids landing under fixed admin subnav.
  }

  public toggleSection(sectionId: string, event?: Event): void {
    event?.preventDefault()
    if (!this.isRootLayout) {
      return
    }
    // Keep one section always open in root jump-to mode.
    this.openSectionId = sectionId
  }

  public leafKind(key: string, value: unknown): OptionsLeafKind {
    if (typeof value === 'boolean') {
      return 'boolean'
    }
    if (typeof value === 'number') {
      return 'number'
    }
    if (Array.isArray(value)) {
      return 'array'
    }
    if (this._looksLikeUrlKey(key)) {
      return 'url'
    }
    if (this._looksLikeHtmlKey(key)) {
      return 'html'
    }
    if (value === null || value === undefined) {
      return this._looksLikeColorKey(key) ? 'color' : 'null'
    }
    if (typeof value === 'string' && (this._looksLikeColorKey(key) || this._isHexColor(value))) {
      return 'color'
    }
    return 'string'
  }

  public asBoolean(key: string): boolean {
    return Boolean(this.node[key])
  }

  public setBoolean(key: string, checked: boolean): void {
    this.node[key] = checked
  }

  public asNumber(key: string): number | null {
    const value = this.node[key]
    return typeof value === 'number' && Number.isFinite(value) ? value : null
  }

  public setNumber(key: string, raw: string | number): void {
    if (raw === '' || raw === null || raw === undefined) {
      this.node[key] = null
      return
    }
    const num = typeof raw === 'number' ? raw : Number(raw)
    this.node[key] = Number.isFinite(num) ? num : this.node[key]
  }

  public asString(key: string): string {
    const value = this.node[key]
    if (value === null || value === undefined) {
      return ''
    }
    return String(value)
  }

  public setString(key: string, raw: string): void {
    this.node[key] = raw
  }

  public setNullableString(key: string, raw: string): void {
    this.node[key] = raw.trim() === '' ? null : raw
  }

  public urlError(key: string): string | null {
    return this.urlErrors[this.pathFor(key)] || null
  }

  public setUrl(key: string, raw: string): void {
    const path = this.pathFor(key)
    const trimmed = raw.trim()
    if (trimmed === '') {
      this.node[key] = null
      if (this.urlErrors[path]) {
        const next = { ...this.urlErrors }
        delete next[path]
        this.urlErrors = next
      }
      return
    }
    if (!this._isValidUrl(trimmed)) {
      this.node[key] = trimmed
      this.urlErrors = { ...this.urlErrors, [path]: 'Enter a valid URL (https://...)' }
      return
    }
    this.node[key] = trimmed
    if (this.urlErrors[path]) {
      const next = { ...this.urlErrors }
      delete next[path]
      this.urlErrors = next
    }
  }

  public colorPickerValue(key: string): string {
    const value = this.node[key]
    if (typeof value === 'string' && this._isHexColor(value)) {
      return value.length === 4 ? `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}` : value.slice(0, 7)
    }
    return '#000000'
  }

  public setColor(key: string, raw: string): void {
    if (this.node[key] === raw) {
      return
    }
    this.node[key] = raw
  }

  public arrayText(key: string): string {
    try {
      return JSON.stringify(this.node[key] ?? [], null, 2)
    } catch {
      return '[]'
    }
  }

  public onArrayBlur(key: string, event: Event): void {
    const raw = (event.target as HTMLTextAreaElement)?.value ?? ''
    try {
      const parsed = JSON.parse(raw)
      if (!Array.isArray(parsed)) {
        return
      }
      this.node[key] = parsed
    } catch {
      // keep previous value until JSON is valid
    }
  }

  /** Exclude this key from the object (queued as unset). Can be restored via Add. */
  public excludeKey(key: string): void {
    this.unsetPath.emit(this.pathFor(key))
  }

  public addKey(key: string): void {
    this.restorePath.emit(this.pathFor(key))
  }

  public onChildUnset(path: string): void {
    this.unsetPath.emit(path)
  }

  public onChildRestore(path: string): void {
    this.restorePath.emit(path)
  }

  public objectChild(key: string): Record<string, unknown> {
    const value = this.node[key]
    return this.isPlainObject(value) ? value : {}
  }

  public objectDefaults(key: string): Record<string, unknown> | null {
    if (!this.defaults || !this.isPlainObject(this.defaults[key])) {
      return null
    }
    return this.defaults[key] as Record<string, unknown>
  }

  /** Gate key on a nested object (`enable` / `enabled` / `status`), if present. */
  public gateKeyFor(key: string): string | null {
    const value = this.node[key]
    if (!this.isPlainObject(value)) {
      return null
    }
    return this._gateKey(value)
  }

  public gateKeysToHide(key: string): string[] {
    const gate = this.gateKeyFor(key)
    return gate ? [gate] : []
  }

  public isSectionExpanded(key: string): boolean {
    const gate = this.gateKeyFor(key)
    if (!gate) {
      return true
    }
    return Boolean(this.objectChild(key)[gate])
  }

  public asObjectGate(key: string): boolean {
    const gate = this.gateKeyFor(key)
    if (!gate) {
      return false
    }
    return Boolean(this.objectChild(key)[gate])
  }

  public setObjectGate(key: string, checked: boolean): void {
    // Theme off removes the whole object (unset), same as Exclude. Sending enable:false would keep and replace the stored theme.
    if (!checked && key === 'theme') {
      this.excludeKey(key)
      return
    }
    const gate = this.gateKeyFor(key)
    if (!gate) {
      return
    }
    this.objectChild(key)[gate] = checked
  }

  private _rebuild(): void {
    this.sections = this._buildSections()
    if (this.isRootLayout) {
      const stillOpen = this.openSectionId && this.sections.some((section) => section.id === this.openSectionId)
      if (!stillOpen) {
        const withData = this.sections.find((section) => section.keys.length > 0)
        this.openSectionId = withData?.id ?? this.sections[0]?.id ?? null
      }
    } else {
      this.openSectionId = null
    }
  }

  private _buildSections(): RenderSection[] {
    const hide = new Set(this.hideKeys || [])
    const presentKeys = Object.keys(this.node || {}).filter((key) => !hide.has(key))
    const defaults = this.defaults

    if (this.pathPrefix || !this.groups?.length) {
      const missingKeys = defaults ? Object.keys(defaults).filter((key) => !hide.has(key) && !Object.prototype.hasOwnProperty.call(this.node, key)) : []
      if (!presentKeys.length && !missingKeys.length) {
        return []
      }
      return [
        {
          id: 'all',
          title: null,
          keys: [...presentKeys].sort((a, b) => a.localeCompare(b)),
          missingKeys: missingKeys.sort((a, b) => a.localeCompare(b)),
          note: undefined
        }
      ]
    }

    const claimed = new Set<string>()
    const sections: RenderSection[] = []

    for (const group of this.groups) {
      const keys = group.keys.filter((key) => Object.prototype.hasOwnProperty.call(this.node, key) && !hide.has(key))
      const missingKeys = defaults
        ? group.keys.filter((key) => !hide.has(key) && Object.prototype.hasOwnProperty.call(defaults, key) && !Object.prototype.hasOwnProperty.call(this.node, key))
        : []
      if (!keys.length && !missingKeys.length) {
        continue
      }
      group.keys.forEach((key) => claimed.add(key))
      sections.push({
        id: group.id,
        title: group.title,
        keys: [...keys],
        missingKeys: [...missingKeys],
        note: group.note
      })
    }

    if (this.hideOther) {
      return sections
    }

    const otherKeys = presentKeys.filter((key) => !claimed.has(key)).sort((a, b) => a.localeCompare(b))
    const otherMissing = defaults
      ? Object.keys(defaults)
          .filter((key) => !claimed.has(key) && !hide.has(key) && !Object.prototype.hasOwnProperty.call(this.node, key))
          .sort((a, b) => a.localeCompare(b))
      : []
    if (otherKeys.length || otherMissing.length) {
      sections.push({
        id: 'other',
        title: 'Other',
        keys: otherKeys,
        missingKeys: otherMissing
      })
    }

    return sections
  }

  private _gateKey(obj: Record<string, unknown>): string | null {
    for (const key of GATE_KEYS) {
      if (Object.prototype.hasOwnProperty.call(obj, key) && typeof obj[key] === 'boolean') {
        return key
      }
    }
    return null
  }

  private _looksLikeHtmlKey(key: string): boolean {
    return /Message$/.test(key)
  }

  private _looksLikeUrlKey(key: string): boolean {
    return /(link|url|logo|front_fixed_element)$/i.test(key)
  }

  private _isValidUrl(value: string): boolean {
    try {
      const parsed = new URL(value)
      return parsed.protocol === 'http:' || parsed.protocol === 'https:'
    } catch {
      return false
    }
  }

  private _looksLikeColorKey(key: string): boolean {
    return /(color|colour|palette|cardBackground|barcodeColor)$/i.test(key) || /^(background|button)$/i.test(key)
  }

  private _isHexColor(value: string): boolean {
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value.trim())
  }
}
